import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "payment-journal-column-widths";
export const MIN_WIDTH = 80;
export const MAX_WIDTH = 900;
const KEY_STEP = 40;

const clamp = (value) => Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value)));

// A per-viewer convenience: storage may be blocked or empty, and the table must
// render the same without it.
const readStored = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const store = (widths) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {
    /* storage unavailable — the width still applies for this visit */
  }
};

/**
 * Drag-to-resize widths for the columns of the journal table.
 * `defaults` maps column key → width in px; only those keys are resizable.
 * Arrow keys on the handle step by 40px, double-click resets to the default.
 */
const useColumnWidths = (defaults) => {
  const [widths, setWidths] = useState(() => {
    const stored = readStored();
    return Object.fromEntries(Object.entries(defaults).map(([key, width]) => [
      key, Number.isFinite(stored[key]) ? clamp(stored[key]) : width,
    ]));
  });
  const widthsRef = useRef(widths);
  widthsRef.current = widths;
  const dragRef = useRef(null);

  const setWidth = useCallback((key, value, { persist = false } = {}) => {
    setWidths((current) => {
      const next = { ...current, [key]: clamp(value) };
      if (persist) store(next);
      return next;
    });
  }, []);

  useEffect(() => () => dragRef.current?.stop(), []);

  const startDrag = useCallback((key, event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = widthsRef.current[key];
    const move = (e) => setWidth(key, startWidth + e.clientX - startX);
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      document.body.classList.remove("payment-journal-resizing");
      dragRef.current = null;
      store(widthsRef.current);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    document.body.classList.add("payment-journal-resizing");
    dragRef.current = { stop };
  }, [setWidth]);

  const handleProps = useCallback((key, label) => ({
    role: "separator",
    tabIndex: 0,
    "aria-orientation": "vertical",
    "aria-label": `Resize ${label} column`,
    "aria-valuemin": MIN_WIDTH,
    "aria-valuemax": MAX_WIDTH,
    "aria-valuenow": widths[key],
    title: "Drag to resize · double-click to reset",
    onPointerDown: (event) => startDrag(key, event),
    onDoubleClick: () => setWidth(key, defaults[key], { persist: true }),
    onKeyDown: (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      setWidth(key, widths[key] + (event.key === "ArrowRight" ? KEY_STEP : -KEY_STEP), { persist: true });
    },
  }), [defaults, startDrag, setWidth, widths]);

  return { widths, handleProps };
};

export default useColumnWidths;
